import { useMutation } from '@apollo/client';
import { useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useCountry } from '../../components/CountrySelector';
import { useOrderSummary } from '../useOrderSummary';
import { OrderState, UpdateOrderAction } from '../useUpdateOrder';
import { UPDATE_ORDER_REWARDS_USE_MUTATION } from './siteCreditOrRewardsSecondPayment.rest';

export const useSiteCreditOrRewardsSecondPayment = () => {
  const {
    country: { alpha3Code }
  } = useCountry();
  const { orderNumber = '' } = useParams<{ orderNumber?: string }>();
  const { orderSummary } = useOrderSummary(orderNumber);

  const [mutate, { loading, error }] = useMutation(UPDATE_ORDER_REWARDS_USE_MUTATION);

  const switchProp = useCallback(
    (property: 'rewards' | 'site-credit') => {
      if (!orderSummary) {
        return Promise.reject('updateOrderRewardsUse: orderSummary is not fetched');
      }

      if (!orderSummary.attributes.selectedItemsIds) {
        return Promise.reject('updateOrderRewardsUse: selectedItemIds is null or empty');
      }

      const {
        id,
        attributes: { useMoRewards, useSiteCredit, selectedItemsIds }
      } = orderSummary;

      return mutate({
        variables: {
          orderId: id,
          input: {
            data: {
              action_type: UpdateOrderAction.UpdatePayment,
              id: id,
              type: 'order_summary',
              attributes: {
                country_code: alpha3Code,
                use_mo_rewards: property === 'rewards' ? !useMoRewards : useMoRewards,
                use_site_credit: property === 'site-credit' ? !useSiteCredit : useSiteCredit,
                order_state: OrderState.PayRemainingBalance,
                selected_item_ids: selectedItemsIds
              }
            }
          }
        }
      });
    },
    [alpha3Code, mutate, orderSummary]
  );

  const applyRewards = useCallback(() => switchProp('rewards'), [switchProp]);

  const applySiteCredit = useCallback(() => switchProp('site-credit'), [switchProp]);

  return { applyRewards, applySiteCredit, loading, error };
};
